import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Button, Drawer, Form, Input, Rate, Typography, notification, Upload } from 'antd';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { createFeedback } from '../../actions/feedbackActions';
import { FEEDBACK_CREATE_RESET } from '../../constants/feedbackConstants';
import { MdReviews } from "react-icons/md";

const { TextArea } = Input;
const { Text, Link } = Typography;

const FeedbackButton = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [form] = Form.useForm();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [screenshot, setScreenshot] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [ratingValue, setRatingValue] = useState(0);

    // Get user info from localStorage
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));

    // Get feedback creation state from Redux
    const feedbackCreate = useSelector((state) => state.feedbackCreate);
    const { loading, success, error } = feedbackCreate;

    useEffect(() => {
        if (success) {
            notification.success({
                message: 'Thank you for your feedback!',
                description: 'Your feedback has been submitted successfully.',
            });
            setIsDrawerOpen(false);
            form.resetFields();
            dispatch({ type: FEEDBACK_CREATE_RESET });
        }
        if (error) {
            notification.error({
                message: 'Error',
                description: error,
            });
        }
    }, [success, error, dispatch, form]);

    // Utility to compress image before base64
    const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.7) => {
        return new Promise((resolve) => {
            const img = new window.Image();
            const reader = new FileReader();
            reader.onload = (e) => {
                img.onload = () => {
                    let width = img.width;
                    let height = img.height;
                    if (width > maxWidth || height > maxHeight) {
                        if (width / height > maxWidth / maxHeight) {
                            height = Math.round((height * maxWidth) / width);
                            width = maxWidth;
                        } else {
                            width = Math.round((width * maxHeight) / height);
                            height = maxHeight;
                        }
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', quality));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    const handleScreenshotChange = async ({ fileList }) => {
        setFileList(fileList);
        if (fileList.length > 0 && fileList[0].originFileObj) {
            const compressed = await compressImage(fileList[0].originFileObj);
            setScreenshot(compressed);
        } else {
            setScreenshot(null);
        }
    };

    const applicationUser = userInfo?.applicationUserProfile;
    const applicationUserFullName = applicationUser ? `${applicationUser.title ? applicationUser.title + ' ' : ''}${applicationUser.firstName || ''} ${applicationUser.lastName || ''}`.trim() : userInfo?.userName;

    const getRatingLabel = (value) => {
        if (value <= 2) return 'Very Bad';
        if (value <= 4) return 'Bad';
        if (value <= 6) return 'Average';
        if (value <= 8) return 'Good';
        if (value <= 9) return 'Very Good';
        if (value === 10) return 'Excellent';
        return '';
    };

    const handleSubmit = (values) => {
        const feedbackData = {
            userId: userInfo.userId,
            userName: applicationUserFullName || values.fullName || userInfo.userName,
            message: values.message,
            rating: ratingValue,
            contactEmail: userInfo.email,
            page: location.pathname,
            source: 'Web App',
            createdAt: new Date().toISOString(),
            screenshot: screenshot || null
        };
        dispatch(createFeedback(feedbackData));
    };

    return (
        <>
            <Button
                type="primary"
                shape="circle"
                icon={<MdReviews size={28} />}
                size="large"
                onClick={() => setIsDrawerOpen(true)}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    width: '56px',
                    height: '56px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000
                }}
            />
            <span
                style={{
                    position: 'fixed',
                    bottom: '34px',
                    right: '90px',
                    background: 'rgba(255,255,255,0.95)',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                    fontWeight: 500,
                    fontSize: 16,
                    color: '#333',
                    zIndex: 1000
                }}
            >
                Feedback
            </span>

            <Drawer
                title="Send Feedback"
                placement="right"
                onClose={() => setIsDrawerOpen(false)}
                open={isDrawerOpen}
                width={400}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        fullName: applicationUserFullName,
                        userId: userInfo?.id,
                        rating: 0
                    }}
                >
                    {!applicationUserFullName && (
                        <Form.Item
                            name="fullName"
                            label="Full Name"
                            rules={[{ required: true, message: 'Please enter your name' }]}
                        >
                            <Input placeholder="Enter your full name" />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="rating"
                        label="How would you rate your experience?"
                        rules={[{ required: true, message: 'Please rate your experience' }]}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Rate
                                count={10}
                                value={ratingValue}
                                onChange={setRatingValue}
                            />
                            {ratingValue > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
                                    <span style={{ minWidth: 48, fontWeight: 'bold', fontSize: 18, textAlign: 'center' }}>
                                        {`${ratingValue}/10`}
                                    </span>
                                    <span style={{ minWidth: 90, color: '#888', fontSize: 16, textAlign: 'center', marginLeft: 12 }}>
                                        {getRatingLabel(ratingValue)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </Form.Item>

                    <Form.Item name="rating" noStyle>
                        <Input type="hidden" value={ratingValue} />
                    </Form.Item>

                    <Form.Item
                        name="message"
                        label="Message"
                        rules={[{ required: true, message: 'Please enter your feedback' }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="Tell us what you think..."
                        />
                    </Form.Item>

                    <Form.Item
                        label="Attach Screenshot (optional)"
                        name="screenshot"
                    >
                        <Upload
                            accept="image/*"
                            listType="picture-card"
                            fileList={Array.isArray(fileList) ? fileList : []}
                            beforeUpload={() => false}
                            onChange={handleScreenshotChange}
                            maxCount={1}
                            showUploadList={{ showPreviewIcon: false }}
                        >
                            {(!fileList || fileList.length === 0) && (
                                <div>
                                    <CameraOutlined style={{ fontSize: 24 }} />
                                    <div style={{ marginTop: 8 }}>
                                        Capture or Upload
                                    </div>
                                </div>
                            )}
                        </Upload>
                        {screenshot && (
                            <img
                                src={screenshot}
                                alt="Screenshot preview"
                                style={{ maxWidth: '100%', marginTop: 8, borderRadius: 4 }}
                            />
                        )}
                    </Form.Item>

                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                        Some account and system information may be sent to OpenEP|NET. We will use it to fix problems and improve our services, subject to our{' '}
                        <Link href="https://aro.tech/privacy-policy">Privacy Policy</Link> and{' '}
                        <Link href="https://aro.tech/terms">Terms of Service</Link>. We may email you for more information or updates.{' '}
                        <Link href="https://aro.tech/legal-help">Go to Legal Help</Link> to ask for content changes for legal reasons.
                    </Text>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading} block>
                            Submit Feedback
                        </Button>
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    );
};

export default FeedbackButton;
